import { useState } from "react";
import { AppSettings, Currency, CURRENCY_SYMBOLS } from "@/lib/storage";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Zap, FileText, ChevronDown, ChevronUp, ExternalLink, Loader2, Sun, Moon, Monitor, DollarSign } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTheme } from "next-themes";

// PAYMENT_FLOW_MERGE_ALERT: isActive / isTrial mirror useAuth.subscription only (from
// check-subscription). Do not merge separate "paid" logic here — see docs/PAYMENT_FLOW.md.

interface Props {
  settings: AppSettings;
  onUpdate: (settings: AppSettings) => void;
}

export default function SettingsTab({ settings, onUpdate }: Props) {
  const appVersion =
    typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "2.2.0";
  const { subscription, checkSubscription } = useAuth();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { setTheme, theme } = useTheme();

  const isActive = subscription.subscribed && !subscription.isTrial;
  const isTrial = subscription.subscribed && subscription.isTrial;
  const trialDaysLeft = subscription.trialEnd
    ? Math.max(0, Math.ceil((new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast.error("Failed to start checkout: " + (err.message || "Unknown error"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast.error("Failed to open billing portal: " + (err.message || "Unknown error"));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
    onUpdate({ ...settings, theme: value as AppSettings['theme'] });
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <div className="p-5 space-y-5 animate-fade-up">
      {/* Subscription */}
      <div className="rounded-xl bg-gradient-to-br from-primary/5 to-accent/5 border border-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-primary" />
          <h4 className="font-display font-bold text-sm text-foreground">Subscription</h4>
        </div>

        {isActive ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Active</span>
              {subscription.subscriptionEnd && (
                <span className="text-muted-foreground text-xs">
                  Renews {new Date(subscription.subscriptionEnd).toLocaleDateString()}
                </span>
              )}
            </div>
            <button
              onClick={handleManage}
              disabled={portalLoading}
              className="w-full py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Manage Subscription
            </button>
          </div>
        ) : isTrial ? (
          <div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-foreground font-medium">{trialDaysLeft} days left</span>
              <span className="text-muted-foreground">in free trial</span>
            </div>
            <div className="mt-3 h-2 bg-border rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(trialDaysLeft / 7) * 100}%` }} />
            </div>
            <button
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              className="mt-3 w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Upgrade — $10/mo
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-destructive/10 text-destructive">Expired</span>
            </div>
            <button
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Subscribe — $10/mo
            </button>
          </div>
        )}

        {!subscription.loading && !isActive && (
          <button
            onClick={checkSubscription}
            className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Refresh subscription status
          </button>
        )}
      </div>

      {/* Theme */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sun className="w-4 h-4 text-muted-foreground" />
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Appearance</Label>
        </div>
        <div className="flex gap-2">
          {themeOptions.map(opt => {
            const Icon = opt.icon;
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleThemeChange(opt.value)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                  active
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/20'
                }`}
              >
                <Icon className="w-4 h-4" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Currency */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Currency</Label>
        </div>
        <Select
          value={settings.currency || 'USD'}
          onValueChange={(v) => onUpdate({ ...settings, currency: v as Currency })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(CURRENCY_SYMBOLS) as Currency[]).map(c => (
              <SelectItem key={c} value={c}>{CURRENCY_SYMBOLS[c]} {c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Privacy Policy */}
      <div>
        <button
          onClick={() => setShowPrivacy(!showPrivacy)}
          className="w-full flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-sm font-medium text-foreground"
        >
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            Privacy Policy
          </span>
          {showPrivacy ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {showPrivacy && (
          <div className="mt-2 p-4 rounded-lg border border-border bg-card text-sm text-muted-foreground space-y-3 animate-fade-up">
            <h5 className="font-semibold text-foreground">Jobily Privacy Policy</h5>
            <p className="text-xs italic">Last updated: February 2026</p>

            <div>
              <h6 className="font-semibold text-foreground mb-1">1. Data We Collect</h6>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Personal information from your profile (name, email, phone, location)</li>
                <li>Resume data including work experience, education, and skills</li>
                <li>Job application details and URLs you provide</li>
                <li>Generated cover letters and application history</li>
              </ul>
              <p className="text-xs mt-2">
                We collect anonymous usage events (such as which features are used and how often)
                via Google Analytics 4 to improve the Extension. This data is anonymous — it is
                associated with a randomly generated ID, not your name or email. No personally
                identifiable information is sent to Google Analytics.
              </p>
            </div>

            <div>
              <h6 className="font-semibold text-foreground mb-1">2. How We Use Your Data</h6>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Generate personalized cover letters using AI</li>
                <li>Store your profile for repeat use across applications</li>
                <li>Improve our services and user experience</li>
              </ul>
            </div>

            <div>
              <h6 className="font-semibold text-foreground mb-1">3. Data Sharing</h6>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>AI processing providers (for cover letter generation only)</li>
                <li>Cloud infrastructure providers (for secure data storage)</li>
                <li>
                  Google Analytics — anonymous event names and counts, random client ID, session ID
                  (to understand feature usage and improve the Extension).{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Google Privacy Policy
                  </a>
                </li>
                <li>We do <strong className="text-foreground">not</strong> sell your personal data to third parties</li>
              </ul>
            </div>

            <div>
              <h6 className="font-semibold text-foreground mb-1">4. Data Protection & Compliance</h6>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>All data is encrypted in transit and at rest</li>
                <li>We comply with GDPR and CCPA regulations</li>
                <li>You may request data deletion at any time by contacting support</li>
                <li>We retain data only as long as your account is active</li>
              </ul>
            </div>

            <div>
              <h6 className="font-semibold text-foreground mb-1">5. Your Rights</h6>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Access, correct, or delete your personal data</li>
                <li>Withdraw consent for data processing</li>
                <li>Export your data in a portable format</li>
                <li>Lodge a complaint with a data protection authority</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Feedback & Support */}
      <div className="space-y-2">
        <a
          href="https://forms.gle/6Mkfu7WknzbvBqKPA"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center gap-2 py-2 px-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-sm font-medium text-foreground"
        >
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
          Feedback
        </a>
        <a
          href="mailto:team@squareoneinternational.com"
          className="w-full flex items-center gap-2 py-2 px-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-sm font-medium text-foreground"
        >
          <FileText className="w-4 h-4 text-muted-foreground" />
          Contact Support
        </a>
      </div>

      {/* Info */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>{`Jobily v${appVersion} · Browser Extension`}</p>
        <p>AI-powered job application assistant - by Squareone International</p>
      </div>
    </div>
  );
}
